import React, { Suspense, useContext, useEffect } from "react";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { AppContextProvider } from "./context/AppContext";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import { I18nextProvider } from "react-i18next";
import i18n from "./config/i18n";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./assets/styles/custom.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import Loader from "./components/Loader";
import PrivateRoute from "./components/PrivateRoute";
import ScrollToTop from "./components/ScrollToTop";
import SignIn from "./pages/Auth/SignIn";
import Error from "./pages/Error";
import Profile from "./pages/Auth/Profile";
import { onMessage } from "firebase/messaging";
import { messaging } from "./helper/firebase";
import AdminManagement from "./pages/Panel/AdminManagement";
import SubAdminManagement from "./pages/Panel/SubAdminManagement";
import UserManagement from "./pages/Panel/UserManagement";
import UserLocationMapPage from "./pages/Panel/UserLocationMap";
import UserNewLocationMapPage from "./pages/Panel/UserNewLocationMap";
import LocationDashboard from "./pages/Panel/LocationDashboard";
import UserAttendance from "./pages/Panel/UserAttendance";
import DataExport from "./pages/Panel/DataExport";
import NotificationManagement from "./pages/Panel/NotificationManagement";
import ProfileSettings from "./pages/ProfileSettings";
import LoginPage from "./pages/Auth/SignIn";
import useRequest from "./hooks/useRequest";
import apiPath from "./constants/apiPath";

function RoleGuard({ allowedTypes, fallbackPath, children }) {
  const { userProfile } = useContext(AuthContext);
  const type = userProfile?.type || userProfile?.userType;
  if (!type || allowedTypes.includes(type)) {
    return children;
  }
  return <Navigate to={fallbackPath} replace />;
}


window.Buffer = window.Buffer || require("buffer").Buffer;
function App() {
  return (
    <AuthProvider>
      <AppContextProvider>
        <I18nextProvider i18n={i18n}>
          <Suspense fallback={<Loader />}>
            <BrowserRouter>
              <ScrollToTop />
              <ToastContainer closeOnClick={false} />
              <AppRoutes />
            </BrowserRouter>
          </Suspense>
        </I18nextProvider>
      </AppContextProvider>
    </AuthProvider>
  );
}

const AppRoutes = () => {
  const { isLoggedIn, setIsLoggedIn, setUserProfile } = useContext(AuthContext);
  const { request } = useRequest();
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const token = urlParams.get("token");
    if (token) {
      setIsLoggedIn(true);
      localStorage.setItem("token", token);
      request({
        url: apiPath.profile,
        method: "GET",
        onSuccess: (data) => {
          const profile = data?.data;
          const normalized = profile
            ? { ...profile, type: profile.type || profile.userType }
            : profile;
          if (normalized?.userType === "user" || normalized?.type === "user") {
            localStorage.removeItem("token");
            window.location.assign("/login");
            return;
          }
          localStorage.setItem("userProfile", JSON.stringify(normalized));
          setUserProfile(normalized);
        },
        onError: (error) => {
          console.log(error);
        },
      });
    }
  }, []);

  useEffect(() => {
    if (messaging) {
      onMessage(messaging, (payload) => {
        console.log("Message received. ", payload);
      });
    }
  }, [messaging]);

  return (
    <Routes>
      <Route
        path="/login"
        element={isLoggedIn ? <Navigate to="/" /> : <LoginPage />}
      />
      <Route path="/" element={<PrivateRoute />}>
        <Route
          index
          element={
            <RoleGuard allowedTypes={["SuperAdmin"]} fallbackPath="/sub-admin">
              <AdminManagement />
            </RoleGuard>
          }
        />
        <Route
          path="sub-admin"
          element={
            <RoleGuard
              allowedTypes={["SuperAdmin", "Admin"]}
              fallbackPath="/user-management"
            >
              <SubAdminManagement />
            </RoleGuard>
          }
        />
        <Route
          path="location-dashboard"
          element={
            <RoleGuard
              allowedTypes={["SuperAdmin", "Admin", "SubAdmin"]}
              fallbackPath="/user-management"
            >
              <LocationDashboard />
            </RoleGuard>
          }
        />
        <Route path="user-management/:userId/location" element={<UserLocationMapPage />} />
        <Route path="user-management/:userId/attendance" element={<UserAttendance />} />
        <Route path="user-management/:userId/new-location" element={<UserNewLocationMapPage />} />
        <Route path="user-management" element={<UserManagement />} />
        <Route path="data-export" element={<DataExport />} />
        <Route path="notification-management" element={<NotificationManagement />} />
        <Route path="profile-settings" element={<ProfileSettings />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      <Route path="*" element={<Error />} />
    </Routes>
  );
};

export default App;
